import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const APPLICATION_COUNT_CHUNK = 8;
const LEGACY_COLLECTION = "opportunities";
const NEW_COLLECTION = "new_oppurtunity";

export function subscribeToOpportunityBoard(onData, onError) {
  const buckets = {
    [LEGACY_COLLECTION]: [],
    [NEW_COLLECTION]: [],
  };

  const emit = () => {
    onData(
      Object.values(buckets)
        .flat()
        .sort(sortByNewest)
    );
  };

  const handleError = (error) => {
    if (typeof onError === "function") onError(error);
  };

  const unsubLegacy = onSnapshot(
    collection(db, LEGACY_COLLECTION),
    (snapshot) => {
      buckets[LEGACY_COLLECTION] = snapshot.docs.map((entry) =>
        normalizeOpportunity(entry, LEGACY_COLLECTION)
      );
      emit();
    },
    handleError
  );

  const unsubNew = onSnapshot(
    collection(db, NEW_COLLECTION),
    (snapshot) => {
      buckets[NEW_COLLECTION] = snapshot.docs.map((entry) =>
        normalizeOpportunity(entry, NEW_COLLECTION)
      );
      emit();
    },
    handleError
  );

  return () => {
    unsubLegacy();
    unsubNew();
  };
}

export async function hydrateOpportunityApplicationCounts(
  opportunities = [],
  seedCounts = {},
  onChunk
) {
  const uniqueOpportunities = normalizeCountTargets(opportunities);
  const next = { ...(seedCounts || {}) };

  for (let index = 0; index < uniqueOpportunities.length; index += APPLICATION_COUNT_CHUNK) {
    const chunk = uniqueOpportunities.slice(index, index + APPLICATION_COUNT_CHUNK);

    const results = await Promise.all(
      chunk.map(async (target) => {
        const subcollection = target.sourceCollection === NEW_COLLECTION ? "bids" : "applications";
        try {
          const aggregate = await getCountFromServer(
            collection(db, target.sourceCollection, target.id, subcollection)
          );
          return { opportunityId: target.id, count: aggregate.data().count || 0 };
        } catch (_aggregateError) {
          const fallback = await getDocs(
            collection(db, target.sourceCollection, target.id, subcollection)
          );
          return { opportunityId: target.id, count: fallback.size || 0 };
        }
      })
    );

    results.forEach(({ opportunityId, count }) => {
      next[opportunityId] = Number(count || 0);
    });

    if (typeof onChunk === "function") {
      onChunk({ ...next });
    }
  }

  return next;
}

export async function updateOpportunityModeration(
  opportunity,
  action,
  { moderationLabel, adminContext } = {}
) {
  if (!opportunity?.id) {
    throw new Error("Opportunity id is required");
  }

  const updates = buildActionPayload(opportunity, action, moderationLabel, adminContext);
  await updateDoc(doc(db, sourceCollectionFor(opportunity), opportunity.id), updates);
}

export async function saveOpportunityModerationLabel(
  opportunityOrId,
  moderationLabel,
  adminContext
) {
  const opportunityId =
    typeof opportunityOrId === "string" ? opportunityOrId : opportunityOrId?.id;
  if (!opportunityId) {
    throw new Error("Opportunity id is required");
  }

  const updates = {
    adminModerationLabel: serializeModerationLabel(moderationLabel),
    moderationUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.moderationUpdatedBy = actor;

  await updateDoc(doc(db, sourceCollectionFor(opportunityOrId), opportunityId), updates);
}

function buildActionPayload(opportunity, action, moderationLabel, adminContext) {
  const payload = {
    moderationUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) payload.moderationUpdatedBy = actor;

  if (moderationLabel !== undefined) {
    payload.adminModerationLabel = serializeModerationLabel(moderationLabel);
  }

  switch (String(action || "").toLowerCase()) {
    case "approve":
      payload.approvalStatus = "approved";
      if (String(opportunity?.status || "").toLowerCase() === "hidden") {
        payload.status = "active";
      }
      break;
    case "reject":
      payload.approvalStatus = "rejected";
      break;
    case "hide":
      payload.status = "hidden";
      break;
    case "feature":
      payload.isFeatured = true;
      break;
    case "unfeature":
      payload.isFeatured = false;
      break;
    default:
      throw new Error(`Unsupported opportunity action: ${action}`);
  }

  return payload;
}

function normalizeOpportunity(entry, sourceCollection = LEGACY_COLLECTION) {
  const data = entry.data() || {};
  const isNewOpportunity = sourceCollection === NEW_COLLECTION;
  const title = safeText(data.title || data.projectName, "Untitled opportunity");
  const organizerName = safeText(
    data.organizerName || data.createdByFullName || data.createdByName,
    "Unknown organizer"
  );
  const city = safeText(data.city || data.location, "Unknown city");
  const category = safeText(data.category || data.requirementType, "Uncategorized");
  const venueName = safeText(data.venueName || data.workMode || data.duration, "");
  const description = safeText(data.description, "");
  const organizerId = safeText(data.organizerId || data.createdByUid, "");
  const createdAtMs = toMillis(data.createdAt);
  const eventDateMs = toMillis(data.eventDate || data.neededDate);
  const expiresAtMs = toMillis(data.expiresAt);
  const requiredSkills = uniqueStrings(data.requiredSkills || data.skills);
  const moderationLabel = normalizeModerationLabel(
    data.adminModerationLabel || data.moderationLabel
  );
  const status = normalizeStatus(data.status);
  const approvalStatus = normalizeApprovalStatus(data.approvalStatus);
  const budgetMin = toNumber(data.budgetMin || data.budgetFrom);
  const budgetMax = toNumber(data.budgetMax || data.budgetTo);

  return {
    id: entry.id,
    ...data,
    sourceCollection,
    sourceLabel: isNewOpportunity ? "App opportunity" : "Admin opportunity",
    metricLabel: isNewOpportunity ? "bids" : "applications",
    title,
    organizerName,
    organizerId,
    city,
    category,
    venueName,
    description,
    requiredSkills,
    status,
    approvalStatus,
    isFeatured: Boolean(data.isFeatured),
    moderationLabel,
    budgetMin,
    budgetMax,
    budgetLabel: formatBudgetRange(budgetMin, budgetMax),
    paymentStatus: safeText(data.paymentStatus, isNewOpportunity ? "pending" : ""),
    taskStatus: safeText(data.taskStatus, isNewOpportunity ? "not_started" : ""),
    selectedArtistId: safeText(data.selectedArtistId, ""),
    selectedArtistPhone: safeText(data.selectedArtistPhone, ""),
    acceptedAmount: toNumber(data.acceptedAmount),
    startOtpVerified: Boolean(data.startOtpVerified),
    endOtpVerified: Boolean(data.endOtpVerified),
    createdAtMs,
    eventDateMs,
    expiresAtMs,
    searchText: [
      entry.id,
      sourceCollection,
      title,
      organizerName,
      organizerId,
      city,
      category,
      venueName,
      description,
      status,
      approvalStatus,
      moderationLabel,
      data.paymentStatus,
      data.taskStatus,
      data.createdByPhone,
      data.selectedArtistPhone,
      ...requiredSkills,
    ]
      .join(" ")
      .toLowerCase(),
  };
}

function normalizeCountTargets(opportunities) {
  const seen = new Set();
  const targets = [];

  (opportunities || []).forEach((item) => {
    const id = typeof item === "string" ? item : item?.id;
    if (!id) return;
    const sourceCollection = sourceCollectionFor(item);
    const key = `${sourceCollection}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    targets.push({ id, sourceCollection });
  });

  return targets;
}

function sourceCollectionFor(opportunity) {
  if (opportunity && typeof opportunity === "object") {
    return opportunity.sourceCollection || LEGACY_COLLECTION;
  }
  return LEGACY_COLLECTION;
}

function sortByNewest(a, b) {
  const createdDiff = (b.createdAtMs || 0) - (a.createdAtMs || 0);
  if (createdDiff !== 0) return createdDiff;

  const eventDiff = (b.eventDateMs || 0) - (a.eventDateMs || 0);
  if (eventDiff !== 0) return eventDiff;

  return String(a.title || "").localeCompare(String(b.title || ""));
}

function normalizeApprovalStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  return raw || "pending";
}

function normalizeStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  return raw || "active";
}

function normalizeModerationLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "clean" || raw === "none") return "";
  return raw;
}

function serializeModerationLabel(value) {
  const normalized = normalizeModerationLabel(value);
  return normalized || "";
}

function serializeAdminContext(adminContext) {
  const docId = safeText(adminContext?.docId, "");
  const name = safeText(adminContext?.name, "");
  const role = safeText(adminContext?.role, "");

  if (!docId && !name && !role) return null;

  return {
    ...(docId ? { docId } : {}),
    ...(name ? { name } : {}),
    ...(role ? { role } : {}),
  };
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function toNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function toMillis(value) {
  if (!value) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.seconds === "number") {
    return value.seconds * 1000;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function uniqueStrings(list) {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    )
  );
}

function formatBudgetRange(min, max) {
  const safeMin = Number(min || 0);
  const safeMax = Number(max || 0);

  if (safeMin > 0 && safeMax > 0) {
    return `Rs ${formatCompactMoney(safeMin)} - Rs ${formatCompactMoney(safeMax)}`;
  }

  if (safeMax > 0) {
    return `Up to Rs ${formatCompactMoney(safeMax)}`;
  }

  if (safeMin > 0) {
    return `From Rs ${formatCompactMoney(safeMin)}`;
  }

  return "Not specified";
}

function formatCompactMoney(amount) {
  try {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return String(amount || 0);
  }
}
